import { useListEditing } from './useListEditing';
import { useItemEditing } from './useItemEditing';
import { useCallback } from 'react';
import { validateItemErrors } from '../../utils/validateItemErrors';
/**
 * `useFormList` is a custom hook that manages the state and interactions of a list within a form.
 * It uses 'react-hook-form' for form control and provides functionalities for adding, updating, and removing items in the list.
 *
 * @template T - The type of data for the form. It should extend from FieldValues of 'react-hook-form'.
 * @param {UseFormListProps<T>} props - The properties passed to the useFormList hook. These include:
 * @param {ArrayPath<T>} listPath - The path to the list field in the form.
 * @param {Control<T>} control - The control object from 'react-hook-form', used for managing the state of form fields.
 * @param {CustomField[]} definitionList - An array of field definitions for items in the list.
 * @param {Partial<T>} defaultValues - Default values for new items in the list.
 * @param {UseFormGetValues<T>} getValues - Function to get form values.
 * @param {number | null} activeIndex - The index of the active item being edited, or null if no item is active.
 * @param {(index: number | null) => void} onIndexChange - Function to change the active index.
 *
 * @returns An object containing:
 * - {T[]} list - The current list of items.
 * - {T} item - The item currently being edited.
 * - {() => void} onCancel - Function to cancel the current edit.
 * - {() => void} onAppend - Function to append a new item to the list.
 * - {() => void} onRemove - Function to remove the current item.
 * - {() => void} onUpdate - Function to update the current item.
 * - {(e: React.ChangeEvent<HTMLInputElement>) => void} onChange - Change handler for form inputs.
 * - {(name: string, value: any) => void} onSetValue - Function to set the value of a form field.
 * - {() => void} onReset - Function to reset the form state.
 */
export const useFormList = ({ listPath, control, definitionList, defaultValues, getValues, activeIndex, onIndexChange }) => {
    const { fields: list, append, remove, update } = useListEditing({
        listPath,
        control
    });
    const { item, onChange, onSetValue, onReset } = useItemEditing(defaultValues);
    const onAppend = useCallback(() => {
        let hasError = validateItemErrors(item, definitionList);
        if (!hasError) {
            append(item);
            onReset();
        }
    }, [item, definitionList, onReset, append]);
    const onUpdate = useCallback(() => {
        if (activeIndex === null)
            return;
        const itemList = getValues(listPath);
        const editedItem = itemList[activeIndex];
        let hasError = validateItemErrors(editedItem, definitionList);
        if (!hasError) {
            update(activeIndex, editedItem);
            onIndexChange(null);
        }
        //onReset();
    }, [activeIndex]);
    const onRemove = useCallback(() => {
        if (activeIndex !== null) {
            remove(activeIndex);
            onIndexChange(null);
        }
        onReset();
    }, [activeIndex]);
    const onCancel = useCallback(() => {
        onIndexChange(null);
        onReset();
    }, []);
    return {
        list,
        item,
        onCancel,
        onAppend,
        onRemove,
        onUpdate,
        onChange,
        onSetValue,
        onReset
    };
};
